import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, ChevronRight } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/context/AuthContext";
import { notificationRoute } from "@/lib/notificationRoute";
import { cn, timeAgo } from "@/lib/utils";

export function NotificationBell() {
  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications();
  const { role } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
        aria-expanded={open}
        className="relative h-11 w-11 sm:h-9 sm:w-9 grid place-items-center rounded-md hover:bg-surface2 text-text gold-focus"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-gold text-[10px] font-bold text-black">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl z-50">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-surface">
            <span className="text-sm font-semibold text-text">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead()}
                className="text-[11px] text-gold hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {loading ? (
            <div className="px-4 py-6 text-center text-xs text-muted">Loading…</div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted">
              You're all caught up. ✨
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {notifications.map((n) => {
                const target = notificationRoute(n, role);
                return (
                  <li
                    key={n.id}
                    className={cn(
                      "flex items-stretch",
                      !n.read && "bg-gold-dim/30",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!n.read) markRead(n.id);
                        if (target) {
                          setOpen(false);
                          navigate(target);
                        }
                      }}
                      className={cn(
                        "min-w-0 flex-1 text-left px-4 py-3 hover:bg-surface2 transition-colors flex gap-2.5 items-start",
                        target && "cursor-pointer",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-1.5 h-2 w-2 rounded-full shrink-0",
                          n.read ? "bg-transparent" : "bg-gold",
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-text font-medium">
                          {n.title}
                        </span>
                        {n.body && (
                          <span className="block text-xs text-muted mt-0.5">
                            {n.body}
                          </span>
                        )}
                        <span className="block text-[10px] text-dim mt-1">
                          {timeAgo(n.created_at)}
                        </span>
                      </span>
                      {target && (
                        <ChevronRight className="h-4 w-4 text-dim shrink-0 mt-1" />
                      )}
                    </button>
                    {!n.read && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markRead(n.id);
                        }}
                        aria-label="Mark as read"
                        title="Mark as read (don't open)"
                        className="shrink-0 px-3 grid place-items-center border-l border-border text-dim hover:text-gold hover:bg-surface2 transition-colors"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
